import { useTranslation } from 'react-i18next';
import { Input, Select, message } from 'antd';
import { useState, useEffect } from 'react';
import { useAuthContext } from '@/hooks/auth';
import { sendContactEmail } from '@/api/subtitles';
import SvgIcon from '@/components/SvgIcon';
import logoICon from '@/assets/icons/logo.svg';
import resultIcon from '@/assets/icons/result.svg';
import styles from './index.module.less';
import Upload from './uploadImage';

const { TextArea } = Input;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INQUIRY_TYPES = [
  { value: 'general', labelKey: 'contactTypeGeneral' },
  { value: 'technical', labelKey: 'contactTypeTechnical' },
  { value: 'billing', labelKey: 'contactTypeBilling' },
  { value: 'partnership', labelKey: 'contactTypePartnership' },
  { value: 'enterprisePurchase', labelKey: 'contactTypeEnterprisePurchase' },
  { value: 'feedback', labelKey: 'contactTypeFeedback' },
];

const SOURCE_LABEL_KEYS: Record<string, string> = {
  WINDOWS_APP: 'contactSourceDesktop',
  CHROME_APP: 'contactSourcePlugin',
  WEB: 'contactSourceWeb',
};

// 上传拿到的是 10 分钟有效的 PUT 预签名 URL，直接放进邮件必然过期；
// 剥出 object key 拼成后端代理链接（免登录、点开时实时重签 302 到 S3），保证邮件里的链接永久可用
const toProxyImageUrl = (presignedUrl: string) => {
  try {
    const { pathname } = new URL(presignedUrl);
    const key = pathname.startsWith('/') ? pathname.slice(1) : pathname;
    return `${import.meta.env.VITE_FELO_SUBTITLES_URL}/api/user/feedback/image?key=${encodeURIComponent(key)}`;
  } catch {
    return presignedUrl;
  }
};

function Feedback() {
  const { t } = useTranslation();
  const { state } = useAuthContext();
  const { user } = state;
  const [name, setName] = useState<string>(user?.displayName || '');
  const [emailInput, setEmailInput] = useState<string>(user?.email || '');
  const [company, setCompany] = useState<string>('');
  const [inquiryType, setInquiryType] = useState<string>('feedback');
  const [subject, setSubject] = useState<string>('');
  const [input, setInput] = useState<string>('');
  const [version, setVersion] = useState<string>('');
  const [subType, setSubType] = useState<string>('WEB');
  const [imageList, setImagesList] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [resultSuccess, setResultSuccess] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const from = searchParams.get('from');
    const v = searchParams.get('v');
    if (v) {
      setVersion(v);
    }
    let st = 'WEB';
    if (from === 'desktop') {
      st = 'WINDOWS_APP';
    } else if (from === 'plugin') {
      st = 'CHROME_APP';
    }
    setSubType(st);
  }, []);

  const toHome = () => {
    window.open(import.meta.env.VITE_FELO_HOME_URL);
  };

  const validate = () => {
    const next: Record<string, string> = {};
    if (name.trim().length < 2) {
      next.name = name.trim() ? t('contactNameMinLength') : t('contactNameRequired');
    }
    if (!emailInput.trim()) {
      next.email = t('contactEmailRequired');
    } else if (!EMAIL_REGEX.test(emailInput.trim())) {
      next.email = t('contactEmailInvalid');
    }
    if (!inquiryType) {
      next.type = t('contactTypeRequired');
    }
    if (subject.trim().length < 5) {
      next.subject = subject.trim() ? t('contactSubjectMinLength') : t('contactSubjectRequired');
    }
    if (input.trim().length < 10) {
      next.content = input.trim() ? t('contactMessageMinLength') : t('contactMessageRequired');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildContent = () => {
    const parts: string[] = [input.trim()];
    if (imageList.length) {
      parts.push('', t('contactAttachmentLabel'), ...imageList.map(toProxyImageUrl));
    }
    const sourceLabel = `${t(SOURCE_LABEL_KEYS[subType] || 'contactSourceWeb')} (${subType})`;
    parts.push('', '---', t('contactSourceFooter', { source: sourceLabel, version: version || '—' }));
    return parts.join('\n');
  };

  const submitFeedback = async () => {
    if (submitting) {
      return;
    }
    if (!validate()) {
      return;
    }
    setSubmitting(true);
    try {
      const data = await sendContactEmail({
        name: name.trim(),
        email: emailInput.trim(),
        company: company.trim(),
        type: inquiryType,
        subject: subject.trim(),
        content: buildContent(),
      });
      if (data.status === 200) {
        setResultSuccess(true);
      } else {
        message.error(t('senderror'));
      }
    } catch (error: any) {
      if (error?.response?.status === 401) {
        message.error(t('contactNeedLogin'));
      } else {
        message.error(t('senderror'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = Boolean(
    name.trim() && emailInput.trim() && inquiryType && subject.trim() && input.trim(),
  );

  return (
    <div className={styles.feedbackContent}>
      <div className={styles.feedbackHeader}>
        <img className={styles.logoicon} onClick={toHome} src={logoICon} />
        <div className={styles.menuTitle} onClick={toHome}>{t('menuSubtitles')}</div>
      </div>
      {resultSuccess ? (
        <>
          <img className={styles.resultIcon} src={resultIcon} />
          <div className={styles.resultText}>{t('receivedFeedback')}</div>
        </>
      ) : (
        <div className={styles.feedbackCenterContent}>
          <div className={styles.feedbackCenterText}>{t('feedback')}</div>

          <div className={styles.feedbackMatter}>
            <div className={styles.matterTitle}><span>*</span>{t('contactName')}</div>
            <div className={styles.fieldBox}>
              <Input
                className={styles.fieldBoxInput}
                maxLength={50}
                placeholder={t('contactNamePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            {errors.name && <div className={styles.errorText}>{errors.name}</div>}

            <div className={styles.matterTitle}><span>*</span>{t('contactEmail')}</div>
            <div className={styles.fieldBox}>
              <Input
                className={styles.fieldBoxInput}
                maxLength={100}
                placeholder={t('contactEmailPlaceholder')}
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
              />
            </div>
            {errors.email && <div className={styles.errorText}>{errors.email}</div>}

            <div className={styles.matterTitle}>{t('contactCompany')}</div>
            <div className={styles.fieldBox}>
              <Input
                className={styles.fieldBoxInput}
                maxLength={100}
                placeholder={t('contactCompanyPlaceholder')}
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              />
            </div>

            <div className={styles.matterTitle}><span>*</span>{t('contactType')}</div>
            <Select
              className={styles.fieldSelect}
              value={inquiryType}
              placeholder={t('contactTypePlaceholder')}
              onChange={(value) => setInquiryType(value)}
              options={INQUIRY_TYPES.map((item) => ({ value: item.value, label: t(item.labelKey) }))}
            />
            {errors.type && <div className={styles.errorText}>{errors.type}</div>}

            <div className={styles.matterTitle}><span>*</span>{t('contactSubject')}</div>
            <div className={styles.fieldBox}>
              <Input
                className={styles.fieldBoxInput}
                maxLength={100}
                placeholder={t('contactSubjectPlaceholder')}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>
            {errors.subject && <div className={styles.errorText}>{errors.subject}</div>}

            <div className={styles.matterTitle}><span>*</span>{t('describeProblem')}</div>
            <div className={styles.textAreaContent}>
              <TextArea
                maxLength={1000}
                placeholder={t('feedbackContent')}
                spellCheck={false}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className={styles.textareaMatter}
              />
            </div>
            <div className={styles.maxCountText}>{input.length}/1000</div>
            {errors.content && <div className={styles.errorText}>{errors.content}</div>}

            <div className={styles.matterTitle}>{t('printscreen')}</div>
            <Upload setImagesList={setImagesList} />
          </div>

          <div className={styles.feedbackMatter}>
            <div className={styles.feedbackButtonContent}>
              <div
                onClick={submitFeedback}
                className={canSubmit && !submitting ? styles.buttonContent : styles.nobuttonContent}
              >
                {submitting ? t('contactSubmitting') : t('send')}
              </div>
            </div>
          </div>

          <div className={styles.feedbackFooter}>
            <div className={styles.footerTitle}>{t('contactUs')}</div>
            <div className={styles.mailbox}>
              {t('mailbox')}：<span><a href="mailto:support@sparticle.com">support@sparticle.com</a></span>
            </div>
            <div className={styles.icons}>
              <a key="facebook" href="https://www.facebook.com/felo.translator/" target="_blank">
                <SvgIcon className={styles.icon} name="facebook" />
              </a>
              <a key="twitter" href="https://twitter.com/FeloApp" target="_blank">
                <SvgIcon className={styles.icon} name="twitter" />
              </a>
              <a key="youtube" href="https://www.youtube.com/channel/UC3_iFnHLJAnJMo99NOAtnpQ" target="_blank">
                <SvgIcon className={styles.icon} name="youtube" />
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Feedback;
